import { usePostHog } from "posthog-js/react";

import { BinocularIcon } from "@/components/Icons/BinocularIcon";
import { CheckIcon } from "@/components/Icons/CheckIcon";
import { Button } from "@/design-system/Button";
import { Inline } from "@/design-system/Inline";
import { Select } from "@/design-system/Select";
import { Switch } from "@/design-system/Switch";
import { Text } from "@/design-system/Text";
import { Tooltip } from "@/design-system/Tooltip";
import { CollectionControls } from "@/product-patterns/tickets/collections/CollectionControls";
import type { AdminTicketTabs, TicketSorting } from "@/queries/tickets/constants";
import { IN_PROGRESS_TICKET_TAB, REMINDER_TICKET_TAB } from "@/queries/tickets/constants";
import { Trans, useTranslation } from "@/translations";

interface TicketTableActionsProps {
  results?: number;
  total?: number;
  onUpdateFocus: (val: boolean) => void;
  sorting: TicketSorting;
  onSort: (sorting: TicketSorting) => void;
  onMarkAsRead: () => void;
  tab: AdminTicketTabs;
  isFocusModeOn: boolean;
  disableSorting?: boolean;
  isFilterActive: boolean;
}
export function TicketTableActions({
  results,
  total,
  onUpdateFocus,
  sorting,
  onSort,
  tab,
  isFocusModeOn,
  disableSorting,
  onMarkAsRead,
  isFilterActive,
}: TicketTableActionsProps): React.ReactNode {
  const { t } = useTranslation();
  const postHog = usePostHog();

  const handleToggleFocusMode = (status: boolean) => {
    onUpdateFocus(status);

    postHog.capture("clicked_toggle_ticket_resident_focus_mode", {
      status,
    });
  };

  const sortingOptions: {
    value: TicketSorting;
    label: string;
  }[] = [
    {
      value: "oldest",
      label: t("page.tickets.table.actions.sort.option.older-first"),
    },
    {
      value: "newest",
      label: t("page.tickets.table.actions.sort.option.newer-first"),
    },
    {
      value: "leastRecentActivity",
      label: t("page.tickets.table.actions.sort.option.least-recent-activity"),
    },
    {
      value: "mostRecentActivity",
      label: t("page.tickets.table.actions.sort.option.most-recent-activity"),
    },
  ];

  return (
    <CollectionControls
      summary={
        <Text variant="caption">
          {results != null && total != null && (
            <Trans
              i18nKey={
                isFilterActive
                  ? "page.tickets.table.actions.results.filtered-label"
                  : "page.tickets.table.actions.results.label"
              }
              values={{
                count: results,
                total: total,
              }}
              components={{
                bold: <b />,
              }}
            />
          )}
        </Text>
      }
      options={
        <>
          {tab === IN_PROGRESS_TICKET_TAB && (
            <Tooltip tooltip={t("page.tickets.table.actions.focus.tooltip")}>
              <Inline align="center" wrap={false}>
                <Switch
                  size="lg"
                  innerLabel={t("page.tickets.table.actions.focus.label")}
                  isChecked={isFocusModeOn}
                  onChange={(e) => handleToggleFocusMode(e.currentTarget.checked)}
                  icon={<BinocularIcon size={20} />}
                />
              </Inline>
            </Tooltip>
          )}
          {tab !== REMINDER_TICKET_TAB && (
            <Inline gap="sm" align="center" wrap={false}>
              <Text variant="caption">{t("page.tickets.table.actions.sort.label")}</Text>

              <Select
                items={sortingOptions}
                selected={sortingOptions.find((x) => x.value === sorting)}
                onChange={(x) => onSort(x.value)}
                renderOption={(x) => x.label}
                keySelector={(x) => x.value!}
                placement="bottom-right"
                disabled={disableSorting}
              />
            </Inline>
          )}
        </>
      }
      action={
        <Button variant="soft" onClick={onMarkAsRead} icon={<CheckIcon />}>
          {t("page.tickets.table.actions.results.mark-all-read")}
        </Button>
      }
    />
  );
}
